import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { BaseLivlyApiResponse } from "../types/Base";
import { BASE_API_URL } from "../utils/constants";
import { Spinner } from "../components/Spinner";
import toLocalTime from "../utils/toLocalTime";
import useLivlyUser from "../context/UserProvider";
import { getIsFeedbackTabsVisible } from "./feedback";
import { Link, Navigate, Outlet } from "react-router";
import moment from "moment-timezone";
import { Button } from "@/components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Review {
  id: number;
  title: string;
  timestamp: string;
  text: string;
  rating: number;
}

type FeedbackEntry = Review & {
  entryType?: string;
  surveyId?: number;
  surveyStatus?: string | number | null;
  expiresAt?: string | null;
  // Completed entries only: disambiguates recurring surveys sharing the same surveyId (HENNY-8456)
  surveyResponseId?: number | null;
};

async function getReviews(leaseId: number) {
  const result = await axios.get<BaseLivlyApiResponse<FeedbackEntry[]>>(
    `${BASE_API_URL}/resident/feedback?leaseId=${leaseId}&entryType=survey`
  );

  return result.data.Data;
}

const feedbackQuery = (leaseId: number) => ({
  queryKey: ["surveys", leaseId],
  queryFn: () => getReviews(leaseId),
  refetchOnWindowFocus: false,
});

export default function SurveysPage() {
  const { user } = useLivlyUser();
  const { data, isLoading } = useQuery({
    ...feedbackQuery(user.propertyUnitLeaseId),
  });
  const entries = (data ?? []) as FeedbackEntry[];
  const { pollsEnabled, feedbackEnabled, surveyEnabled } = getIsFeedbackTabsVisible(
    user.building.serviceTypes ?? []
  );

  if (!surveyEnabled) {
    if (feedbackEnabled) {
      return <Navigate to="../reviews" replace />;
    }

    if (pollsEnabled) {
      return <Navigate to="../polls" replace />;
    }

    return null;
  }

  if (pollsEnabled && !feedbackEnabled) {
    return <Navigate to="../polls" replace />;
  }

  // API now filters by entryType=survey server-side, so entries are already surveys only
  const surveys = entries;

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center flex-1 my-56">
          <Spinner color="livly" size="xl" />
        </div>
      ) : (
        <div>
          {surveys.length === 0 ? (
            <div className="my-10 text-center">
              <h4 className="text-xl">No surveys yet</h4>
              <p className="mt-2 text-sm font-light">
                Any surveys you receive will appear here
              </p>
            </div>
          ) : (
            <ul className="text-sm divide-y divide-gray-200">
              {surveys.map((entry) => {
                const surveyStatus = String(entry.surveyStatus ?? "").toLowerCase();
                const isPending = surveyStatus === "pending";
                const isCompleted = surveyStatus === "completed";
                const isMarkedExpired = surveyStatus === "expired";
                // No expiresAt means the survey never expires — don't fall back to the creation timestamp
                const isExpired = Boolean(
                  isMarkedExpired ||
                    (isPending &&
                      entry.expiresAt &&
                      !moment(entry.expiresAt).isAfter(moment(), "day"))
                );

                const status: "completed" | "expired" | "pending" = isExpired
                  ? "expired"
                  : isCompleted
                    ? "completed"
                    : "pending";

                const subtitle =
                  status === "completed"
                    ? entry.timestamp
                      ? `Completed ${toLocalTime(entry.timestamp).format("MMM D, YYYY")}`
                      : "Completed"
                    : entry.timestamp
                      ? `Survey • ${toLocalTime(entry.timestamp).fromNow()}`
                      : "Survey";

                const hasValidSurveyId =
                  typeof entry.surveyId === "number" && entry.surveyId > 0;

                const surveyResponseIdParam =
                  status === "completed" && typeof entry.surveyResponseId === "number"
                    ? `&surveyResponseId=${entry.surveyResponseId}`
                    : "";

                const href =
                  !hasValidSurveyId
                    ? ""
                    : status === "completed"
                    ? `/lease/${user.propertyUnitLeaseId}/surveys/${entry.surveyId}/response?from=surveys${surveyResponseIdParam}`
                    : status === "pending"
                    ? `/lease/${user.propertyUnitLeaseId}/surveys/${entry.surveyId}?from=surveys`
                    : "";

                const statusBadge =
                  status === "completed" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                      <FontAwesomeIcon icon="check" className="text-[10px]" />
                      Completed
                    </span>
                  ) : status === "expired" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                      <FontAwesomeIcon icon="hourglass-clock" className="text-[10px]" />
                      Expired
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                      <FontAwesomeIcon icon="clipboard-question" className="text-[10px]" />
                      Pending
                    </span>
                  );

                return (
                  <li
                    key={
                      status === "completed" && entry.surveyResponseId != null
                        ? `${entry.surveyId}-${entry.surveyResponseId}`
                        : (entry.surveyId ?? entry.id)
                    }
                    className="py-3 hover:bg-gray-50"
                  >
                    {status === "expired" || !hasValidSurveyId ? (
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 truncate">
                              {entry.title ?? "Survey"}
                            </p>
                            {statusBadge}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
                        </div>

                        <span className="text-xs font-medium text-amber-700">
                          {status === "expired" ? "Expired" : "Unavailable"}
                        </span>
                      </div>
                    ) : (
                      <Link
                        to={href}
                        state={{ from: "surveys" }}
                        className="flex items-center justify-between gap-4"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 truncate">
                              {entry.title ?? "Survey"}
                            </p>
                            {statusBadge}
                          </div>
                          <p className="mt-1 text-xs text-gray-500">{subtitle}</p>
                        </div>

                        {status === "pending" ? (
                          <Button size="small" color="secondary">
                            Complete Survey
                          </Button>
                        ) : (
                          <Button size="small" color="default">
                            View Response
                          </Button>
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <Outlet />
    </>
  );
}
